import { useEffect, useState } from "react";
import { AuthenticateWithRedirectCallback } from "@clerk/react";
import { Link } from "@tanstack/react-router";
import { buttonVariants } from "@/components/ui/button";
import { CartaoEntrada } from "@/components/entrada/CartaoEntrada";
import { MolduraEntrada } from "@/components/entrada/MolduraEntrada";
import { Spinner } from "@/components/ui/spinner";
import { Icone } from "@/components/Icone";
import { FormCadastro } from "./FormCadastro";
import { FormEntrar } from "./FormEntrar";

/**
 * As telas de entrada: login, cadastro e a volta do Google. Nossas, em
 * shadcn — nenhum componente visual do Clerk entra na tela.
 */
export function Entrar() {
  return (
    <MolduraEntrada nota="Responde em segundos, a qualquer hora. Entende o que a pessoa quer, confere a agenda de verdade, marca o horário e abre a ficha. Você vê tudo por aqui — e vê quantas conversas foram atendidas sem você precisar entrar.">
      <FormEntrar />
    </MolduraEntrada>
  );
}

/**
 * A página inicial para quem ainda não entrou — o que é o LummiChat e os dois
 * caminhos. Quem já entrou nem a vê: vai direto para o painel (rotas.tsx).
 */
export function Inicio() {
  return (
    <MolduraEntrada nota="Responde em segundos, a qualquer hora. Entende o que a pessoa quer, confere a agenda de verdade, marca o horário e abre a ficha. Quando precisa de alguém da equipe, avisa na hora.">
      <CartaoEntrada
        rotulo="Para clínicas"
        titulo="Seu WhatsApp atendendo sozinho"
        descricao="Um assistente virtual que atende os pacientes pelo WhatsApp da clínica, e um painel onde a equipe acompanha tudo."
      >
        <ul className="mb-6 flex flex-col gap-2.5 text-sm text-muted-foreground">
          {[
            ["agenda", "Responde, tira dúvidas e marca horários na agenda da clínica."],
            ["equipe", "Passa para a equipe o que só uma pessoa resolve."],
            ["relatorio", "Mostra quantos atendimentos aconteceram sem ninguém precisar entrar."],
          ].map(([icone, texto]) => (
            <li key={icone} className="flex items-start gap-2.5">
              <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-realce)] text-[var(--color-marca-forte)]">
                <Icone nome={icone as "agenda" | "equipe" | "relatorio"} className="h-3.5 w-3.5" />
              </span>
              {texto}
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-2">
          <Link to="/sign-in" className={buttonVariants({ size: "lg", className: "w-full" })}>
            Entrar
          </Link>
          <Link to="/sign-up" className={buttonVariants({ size: "lg", variant: "outline", className: "w-full" })}>
            Criar conta
          </Link>
        </div>
      </CartaoEntrada>
    </MolduraEntrada>
  );
}

export function Cadastro() {
  return (
    <MolduraEntrada nota="Crie a conta da sua clínica em um minuto. Depois é conectar o número e deixar o assistente atender.">
      <FormCadastro />
    </MolduraEntrada>
  );
}

/**
 * A volta do Google, para o login e para o cadastro.
 *
 * O Clerk devolve o motivo na URL quando o Google falha; a tela de origem sabe
 * traduzir. Sem isto a pessoa voltaria sem explicação nenhuma.
 */
export function RetornoDoGoogle({ de }: { de: "entrada" | "cadastro" }) {
  const [erro, setErro] = useState<string | null>(null);
  const origem = de === "entrada" ? "/sign-in" : "/sign-up";

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const codigo = p.get("err_code") ?? p.get("error") ?? p.get("error_code");
    if (codigo) {
      setErro(codigo);
      window.location.replace(`${origem}?erro=${encodeURIComponent(codigo)}`);
    }
  }, [origem]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="text-primary" />
        {erro
          ? de === "entrada"
            ? "Voltando para o login…"
            : "Voltando para o cadastro…"
          : de === "entrada"
            ? "Concluindo login com Google…"
            : "Concluindo cadastro com Google…"}
      </p>
      {!erro ? <AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/conversas" signUpFallbackRedirectUrl="/conversas" /> : null}
    </div>
  );
}
